import { useCallback, useEffect, useRef } from 'react'
import { getCompanyName } from '../users/user-helper'
import { useGetProfileQuery } from '../users/users.service'
import { useAppSelector } from '../../redux/hooks'
import { selectId } from './auth.slice'

export const FreshChatWidget: React.FC = () => {
    const userId = useAppSelector(selectId)
    const { data } = useGetProfileQuery(userId, {
        skip: userId === null,
    })

    // Keep the latest profile in a ref so the widget:loaded handler always reads
    // current values without needing to re-register the listener.
    const profileRef = useRef(data)
    profileRef.current = data

    // Push the signed-in user's identity (incl. email) so conversations carry it.
    // Idempotent: no-ops until both the widget and the profile are ready.
    const pushIdentity = useCallback(() => {
        const profile = profileRef.current
        if (!window.fcWidget?.user || !profile) return
        try {
            window.fcWidget.user.setProperties({
                externalId: profile.id,
                firstName: profile.name,
                lastName: '',
                email: profile.email,
                role: profile.authContext.role,
                entityId: profile.authContext.entityId,
                entityType: profile.authContext.type,
                companyName: getCompanyName(profile, profile.authContext.entityId),
            })
        } catch (err) {
            console.warn('Failed to set Freshchat user properties', err)
        }
    }, [])

    useEffect(() => {
        let cancelled = false

        // The widget SDK loads async (see index.tsx), so window.fcWidget may not exist yet.
        // Attach once it does: push immediately (covers "widget loaded before profile") and
        // register widget:loaded (covers the reverse — it only fires once).
        const attach = () => {
            if (cancelled || !window.fcWidget) return false
            pushIdentity()
            window.fcWidget.on('widget:loaded', pushIdentity)
            return true
        }

        if (attach()) {
            return () => {
                cancelled = true
                if (window.fcWidget) window.fcWidget.off('widget:loaded', pushIdentity)
            }
        }

        const timer = setInterval(() => {
            if (attach()) clearInterval(timer)
        }, 500)
        const stop = setTimeout(() => clearInterval(timer), 15000)

        return () => {
            cancelled = true
            clearInterval(timer)
            clearTimeout(stop)
            if (window.fcWidget) window.fcWidget.off('widget:loaded', pushIdentity)
        }
    }, [data, pushIdentity])

    return null
}
